import { UserRole } from "constants/userRole";
import { useUserWithAccount } from "context/UserAccountProvider";
import { SIDEBAR_WIDTH } from "layout/components/Sidebar";
import { sizer } from "layout/styles/styled/sizer";
import { Analytics } from "pages/Analytics";
// import { Dashboard } from "pages/Dashboard";
import { OrdersTable } from "pages/Orders/components/OrdersTable";
import { PatientsTable } from "pages/Patients/components/PatientsTable";
import { Providers } from "pages/Providers";
import { ReportsTable } from "pages/Reports/component/ReportsTable";
import { Button } from "primereact/button";
import { TabView, TabPanel } from "primereact/tabview";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { onAdminAccountChange, userSelector } from "store/slices/user";
import styled from "styled-components";
import { TourAnchor } from "training/anchors";
import { TrainingBanner } from "training/components/TrainingBanner";
import { TrainingTourHost } from "training/components/TrainingTourHost";
import { requestTrainingTourStart } from "training/engine/session";
import { IAccount } from "types/Account/Account";

import { PaginatedChatsTable } from "../Chatbot/components/PaginatedChatsTable";
import { ContractsLibrary } from "./components/ContractsLibrary";
import { PlasticsReportsDashboard } from "./components/PlasticsReportsDashboard";

// Tab name constants for named routing
const TAB_NAMES = {
  ORDERS: "orders",
  PROVIDERS: "providers",
  ANALYTICS: "analytics",
  PATIENTS: "patients",
  REPORTS: "reports",
  DOCTOR_REVENUE: "doctor-revenue",
  PATIENT_CHATS: "patient-chats",
  CONTRACTS: "contracts",
} as const;

const BackButton = styled(Button)`
  margin-bottom: ${sizer(4)};
  margin-left: 0.825rem;
`;

const TAB_BAR_HEIGHT = 54;

const TAB_BAR_INSET = 24;

const StyledTabView = styled(TabView)<{
  gray: boolean;
  $hideSidebar: boolean;
  $flushSides: boolean;
}>`
  & > .p-tabview-nav-container {
    position: fixed;
    top: var(--navbar-height);
    left: ${({ $hideSidebar }) => ($hideSidebar ? 0 : SIDEBAR_WIDTH)};
    right: 0;

    z-index: 997;
    background-color: var(--brand-white);

    .p-tabview-nav {
      padding-left: ${({ $hideSidebar }) =>
        $hideSidebar ? TAB_BAR_INSET : 0}px;
    }

    .p-tabview-nav li .p-tabview-nav-link {
      height: ${TAB_BAR_HEIGHT}px;
      font-size: 0.875rem;
      font-weight: var(--font-weight-semibold);
    }
  }

  & > .p-tabview-panels {
    padding-top: 0;
    background-color: transparent;
    /* CLE-2831 — the Estimates table is wide enough to scroll sideways on a
       laptop, and the theme's 1.25rem panel inset sits on top of the page
       gutter on both sides. Dropping it there gives the table that width back. */
    ${({ $flushSides }) => $flushSides && "padding-left: 0; padding-right: 0;"}
  }

  .p-tabview-panels {
    ${(props) => props.gray && `background-color: #f5f5f5;`}
  }
`;

const PageRoot = styled.div`
  padding-top: ${TAB_BAR_HEIGHT}px;
`;

export default function ViewHospitals() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { currentAccount: reduxCurrentAccount } = useSelector(userSelector);
  const { accounts, userInfo } = useUserWithAccount();

  // Extract tab and subtab from the pathname
  const pathParts = location.pathname.split("/").filter(Boolean);
  const idIndex = pathParts.findIndex((part) => part === id);
  const tab =
    idIndex >= 0 && pathParts[idIndex + 1] ? pathParts[idIndex + 1] : undefined;
  const subtab =
    idIndex >= 0 && pathParts[idIndex + 2] ? pathParts[idIndex + 2] : undefined;

  // Sync the Redux currentAccount state with the URL parameter IMMEDIATELY
  // This must happen synchronously before any child components render
  if (id && reduxCurrentAccount !== id) {
    dispatch(onAdminAccountChange({ currentAccount: id }));
  }

  const currentAccount: IAccount | undefined = accounts?.data.find(
    (aAccount) => aAccount.id === id
  );

  const showTabs = useMemo(() => {
    return userInfo?.scope === UserRole.SUPER_ADMIN;
  }, [userInfo]);

  // Build tab list based on permissions
  const availableTabs = useMemo(() => {
    // Contracts-only users (HospitalStaff with contractsOnlyMode) see ONLY Contracts tab
    if (
      userInfo?.scope === UserRole.HOSPITAL_STAFF &&
      userInfo?.contractsOnlyMode
    ) {
      return [TAB_NAMES.CONTRACTS];
    }

    // Reports-only users (HospitalStaff with reportsOnlyMode) see ONLY Reports tab
    if (
      userInfo?.scope === UserRole.HOSPITAL_STAFF &&
      userInfo?.reportsOnlyMode
    ) {
      return [TAB_NAMES.REPORTS];
    }

    // Normal tab visibility for all other users
    const tabs: any[] = [TAB_NAMES.ORDERS, TAB_NAMES.PROVIDERS];

    if (userInfo?.analyticsTabEnabled) {
      tabs.push(TAB_NAMES.ANALYTICS);
    }

    // Patients Across Accounts is only meaningful for hospital staff who can
    // see more than one account; super admins don't need it.
    if (
      userInfo?.scope === UserRole.HOSPITAL_STAFF &&
      (userInfo?.accountIds?.length ?? 0) > 1
    ) {
      tabs.push(TAB_NAMES.PATIENTS);
    }

    tabs.push(TAB_NAMES.REPORTS);

    if (currentAccount?.features?.plasticsReportsEnabled) {
      tabs.push(TAB_NAMES.DOCTOR_REVENUE);
    }

    if (showTabs && currentAccount?.patientChatBotId) {
      tabs.push(TAB_NAMES.PATIENT_CHATS);
    }

    // Contracts tab is always visible to all users
    tabs.push(TAB_NAMES.CONTRACTS);

    return tabs;
  }, [userInfo, showTabs, currentAccount]);

  // Get current tab from URL or default to orders
  const currentTabName = tab || TAB_NAMES.ORDERS;
  const activeIndex = availableTabs.indexOf(currentTabName);
  const activeTabIndex = activeIndex === -1 ? 0 : activeIndex;

  // Handler to update URL when tab changes
  const handleTabChange = (e: { index: number }) => {
    const newTabName = availableTabs[e.index];
    if (newTabName) {
      navigate(`/hospitals/view/${id}/${newTabName}`, { replace: true });
    }
  };

  // Build TabPanels array programmatically to match availableTabs exactly
  const tabPanels = useMemo(() => {
    const panels: JSX.Element[] = [];

    availableTabs.forEach((tabName) => {
      switch (tabName) {
        case TAB_NAMES.ORDERS:
          panels.push(
            <TabPanel
              key="orders"
              header={
                <span data-tour={TourAnchor.EstimatesTab}>Estimates</span>
              }
            >
              <OrdersTable
                accountId={id}
                directPayEnabled={currentAccount?.directPayEnabled}
                hstImportEnabled={currentAccount?.features?.hstImport}
                athenaImportEnabled={currentAccount?.features?.athenaImport}
              />
            </TabPanel>
          );
          break;
        case TAB_NAMES.PROVIDERS:
          panels.push(
            <TabPanel
              key="providers"
              header={
                <span data-tour={TourAnchor.ProvidersTab}>Providers</span>
              }
            >
              <Providers />
            </TabPanel>
          );
          break;
        case TAB_NAMES.ANALYTICS:
          panels.push(
            <TabPanel key="analytics" header="Analytics">
              <Analytics accountId={id} />
            </TabPanel>
          );
          break;
        case TAB_NAMES.PATIENTS:
          panels.push(
            <TabPanel key="patients" header="Patients Across Accounts">
              <PatientsTable showMessage />
            </TabPanel>
          );
          break;
        case TAB_NAMES.REPORTS:
          panels.push(
            <TabPanel
              key="reports"
              header={<span data-tour={TourAnchor.ReportsTab}>Reports</span>}
            >
              <ReportsTable accountId={id} />
            </TabPanel>
          );
          break;
        case TAB_NAMES.DOCTOR_REVENUE:
          panels.push(
            <TabPanel key="doctor-revenue" header="Doctor Revenue">
              <PlasticsReportsDashboard accountId={id ?? ""} />
            </TabPanel>
          );
          break;
        case TAB_NAMES.PATIENT_CHATS:
          panels.push(
            <TabPanel key="patient-chats" header="Patient Chats">
              <PaginatedChatsTable accountId={id} defaultPageSize={20} />
            </TabPanel>
          );
          break;
        case TAB_NAMES.CONTRACTS:
          panels.push(
            <TabPanel
              key="contracts"
              header={
                <span data-tour={TourAnchor.ContractsTab}>Contracts</span>
              }
            >
              <ContractsLibrary
                accountId={id ?? ""}
                accountName={currentAccount?.name}
                subtab={subtab}
              />
            </TabPanel>
          );
          break;
        default:
          break;
      }
    });

    return panels;
  }, [availableTabs, id, currentAccount, subtab]);

  return (
    <PageRoot>
      {userInfo?.scope === UserRole.SUPER_ADMIN ? (
        <BackButton
          className="p-button-neutral p-button-sm p-button-rounded-md"
          icon="pi pi-angle-left"
          label="Back to Hospitals"
          onClick={() => navigate("/hospitals")}
        />
      ) : null}

      {/* CLE-3015 — both render only for uncertified HospitalStaff on an account with the flag on. */}
      <TrainingBanner onStart={requestTrainingTourStart} />
      <TrainingTourHost route="estimates" accountId={id} />

      <StyledTabView
        activeIndex={activeTabIndex}
        className="w-full"
        onTabChange={handleTabChange}
        gray={false}
        $hideSidebar={userInfo?.scope === UserRole.HOSPITAL_STAFF}
        $flushSides={currentTabName === TAB_NAMES.ORDERS}
      >
        {tabPanels}
      </StyledTabView>
    </PageRoot>
  );
}
